import React, { ReactNode, useContext, useMemo } from 'react';
import useClientDetails from '../../../utils/hooks/use_client_details';

interface ClientDataContextTypes {
  isAuthEnabled: boolean,
  areInvitesEnabled: boolean,
  areAssaysEnabled: boolean,
  useArrowLogFormat: boolean,
  isJupyterEnabled: boolean,
  isOrchestrationEnabled: boolean,
  clientName: string,
  error: string | null,
}

const ClientDataContext = React.createContext<ClientDataContextTypes>({
  isAuthEnabled: false,
  areInvitesEnabled: false,
  areAssaysEnabled: false,
  useArrowLogFormat: false,
  isJupyterEnabled: false,
  isOrchestrationEnabled: false,
  clientName: 'Wallaroo',
  error: null,
});

interface Props {
  children?: ReactNode | null
}

export const ClientDataProvider = ({
  children = null
}: Props) => {
  const { clientDetails, error } = useClientDetails();
  const isAuthEnabled = clientDetails?.is_auth_enabled || false;
  const areInvitesEnabled = clientDetails?.are_invites_enabled || false;
  const areAssaysEnabled = clientDetails?.are_assays_enabled || false;
  const useArrowLogFormat = clientDetails?.use_arrow || false;
  const isJupyterEnabled = clientDetails?.is_jupyter_enabled || false;
  const areEventsEnabled = clientDetails?.are_events_enabled || false;
  const areExternalInferenceURLsEnabled = clientDetails?.are_external_inference_urls_enabled || false;
  const isOrchestrationEnabled = clientDetails?.is_orchestration_enabled || false;
  const clientName = clientDetails?.name || 'Wallaroo'

  const clientDataContextValue = useMemo(() => ({
    isAuthEnabled,
    areInvitesEnabled,
    areAssaysEnabled,
    useArrowLogFormat,
    isJupyterEnabled,
    isOrchestrationEnabled,
    areEventsEnabled,
    areExternalInferenceURLsEnabled,
    clientName,
    error
  }), [
    isAuthEnabled,
    areInvitesEnabled,
    areAssaysEnabled,
    useArrowLogFormat,
    isJupyterEnabled,
    isOrchestrationEnabled,
    areEventsEnabled,
    areExternalInferenceURLsEnabled,
    clientName,
    error
  ])

  return (
    <ClientDataContext.Provider value={clientDataContextValue}>
      {children}
    </ClientDataContext.Provider>
  )
}

export const useClientContext = () => useContext(ClientDataContext);

export default useClientContext;
